import { useRef, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  {
    num: '01',
    icon: 'solar:qr-code-bold',
    title: 'QR 스캔',
    desc: '테이블 위 QR코드를 스마트폰으로 스캔하면 앱 설치 없이 바로 메뉴가 열립니다.',
    screen: '/landing/step-1.png',
  },
  {
    num: '02',
    icon: 'solar:cart-3-bold',
    title: '메뉴 선택',
    desc: '사진과 설명이 있는 메뉴에서 원하는 음식을 골라 장바구니에 담습니다.',
    screen: '/landing/step-2.png',
  },
  {
    num: '03',
    icon: 'solar:check-circle-bold',
    title: '주문 끝',
    desc: '주문 버튼 한 번이면 끝. POS에 자동 전송되고 주방 프린터에서 바로 출력됩니다.',
    screen: '/landing/step-3.png',
  },
];

export default function HowItWorks() {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top',
        end: '+=200%',
        pin: true,
        pinSpacing: true,
        scrub: 1.2,
        anticipatePin: 1,
        onUpdate: (self) => setProgress(self.progress),
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile]);

  const activeStep = progress < 0.3 ? 0 : progress < 0.65 ? 1 : 2;

  if (isMobile) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-brand-600 font-semibold text-sm tracking-wide uppercase mb-3">How it works</p>
            <h2 className="text-3xl font-bold leading-tight tracking-tight text-neutral-900 mb-3">
              3단계면 충분합니다
            </h2>
            <p className="text-neutral-500 text-base leading-relaxed">
              앱 설치 없이, 3초면 주문이 시작됩니다.
            </p>
          </div>
          <div className="space-y-10">
            {STEPS.map((s, i) => (
              <div key={s.num} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-brand-100 flex items-center justify-center mx-auto mb-4">
                  <Icon icon={s.icon} className="text-brand-600 text-2xl" />
                </div>
                <p className="text-xs text-brand-600 font-semibold tracking-widest uppercase mb-1">Step {s.num}</p>
                <h3 className="text-xl font-bold text-neutral-900 mb-2">{s.title}</h3>
                <p className="text-neutral-500 text-sm leading-relaxed max-w-xs mx-auto mb-5">{s.desc}</p>
                <div className="mx-auto w-44 rounded-[24px] overflow-hidden shadow-2xl shadow-neutral-900/10 border-4 border-neutral-200">
                  <img src={s.screen} alt={s.title} className="w-full h-auto" />
                </div>
                {i < 2 && (
                  <div className="flex justify-center mt-6">
                    <Icon icon="solar:arrow-down-linear" className="text-2xl text-neutral-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="min-h-[100dvh] bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[100dvh] flex items-center relative z-10">
        <div className="w-full">
          <div className="text-center mb-14">
            <p className="text-brand-600 font-semibold text-sm tracking-wide uppercase mb-3">How it works</p>
            <h2 className="text-3xl lg:text-5xl font-bold leading-tight tracking-tight text-neutral-900 mb-3">
              3단계면 충분합니다
            </h2>
            <p className="text-neutral-500 text-lg">앱 설치 없이, 3초면 주문이 시작됩니다.</p>
          </div>

          <div className="flex items-center justify-center gap-8 lg:gap-12">
            <div className="flex-1 max-w-xs space-y-5">
              {STEPS.map((s, i) => {
                const isActive = i === activeStep;
                const isPast = i < activeStep;
                return (
                  <div
                    key={s.num}
                    className={`flex items-start gap-4 p-4 rounded-2xl transition-all duration-700 ease-out ${
                      isActive ? 'bg-brand-50 border border-brand-200' : 'border border-transparent'
                    }`}
                    style={{
                      opacity: isActive ? 1 : isPast ? 0.4 : 0.35,
                      transform: isActive ? 'translateX(0)' : 'translateX(-8px)',
                      transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-700 ${
                      isActive ? 'bg-brand-400 scale-110' : isPast ? 'bg-brand-200' : 'bg-neutral-100'
                    }`}>
                      {isPast ? (
                        <Icon icon="solar:check-circle-bold" className="text-brand-600 text-lg" />
                      ) : (
                        <Icon icon={s.icon} className={`text-lg ${isActive ? 'text-neutral-900' : 'text-neutral-400'}`} />
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-brand-500 font-semibold tracking-widest uppercase mb-0.5">Step {s.num}</p>
                      <h3 className={`text-base font-bold transition-colors duration-500 ${isActive ? 'text-neutral-900' : 'text-neutral-400'}`}>
                        {s.title}
                      </h3>
                      <p className={`text-sm leading-relaxed mt-1 transition-all duration-700 overflow-hidden ${
                        isActive ? 'text-neutral-500 max-h-20 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        {s.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex-shrink-0 relative">
              <div className="absolute -inset-16 bg-brand-400/5 rounded-full blur-3xl" />
              <div className="relative w-[200px] rounded-[28px] border-[5px] border-neutral-800 bg-neutral-900 shadow-2xl shadow-neutral-900/30 overflow-hidden" style={{ aspectRatio: '1080/2267' }}>
                {STEPS.map((s, i) => (
                  <div
                    key={s.num}
                    className="absolute inset-0 transition-opacity duration-700 ease-out"
                    style={{ opacity: i === activeStep ? 1 : 0 }}
                  >
                    <img
                      src={s.screen}
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 max-w-xs flex flex-col items-center">
              <div className="text-center mb-8">
                <p className="text-6xl font-bold text-neutral-900 transition-all duration-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {activeStep + 1}
                  <span className="text-neutral-300 text-3xl font-normal"> / 3</span>
                </p>
              </div>

              <div className="w-1 h-40 bg-neutral-100 rounded-full overflow-hidden relative">
                <div
                  className="w-full bg-brand-400 rounded-full transition-all duration-700 ease-out"
                  style={{ height: `${((activeStep + 1) / 3) * 100}%` }}
                />
              </div>

              <div className="mt-6 flex flex-col items-center gap-2">
                <Icon icon="solar:mouse-bold" className="text-neutral-300 text-xl" />
                <p className="text-neutral-400 text-xs">스크롤하세요</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
